import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import type { EvidenceFact } from "@digital-twins/shared-types";

import {
  evidenceGroupTitle,
  evidenceSourceKey,
} from "./analystFactNumbering";

import type { FactNumbering } from "./analystFactNumbering";

import { formatAnalystComputation } from "./analystComputationLabel";

import type { SyntheticEvent } from "react";

interface AnalystEvidenceProps {
  evidence: Array<EvidenceFact>;
  messageToken: string;
  numbering: FactNumbering;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  focusFactId: string | null;
}

/**
 * Number-badge visual for a disclosure row, copied from
 * `CITATION_MARKER_CHIP_CLASS` (source: pages/ThreadDetailPage/ThreadCitationMarker.tsx)
 * so a row and its inline chip read as the same object. Copied, not imported:
 * see docs/plans/analyst-thread-alignment.md, "Share tokens, not modules".
 */
const FACT_NUMBER_BADGE_CLASS =
  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none bg-primary/10 text-foreground";

/** How long a focused row stays highlighted after a chip scrolls it into view. */
const FOCUS_HIGHLIGHT_MS = 1500;

/**
 * The `t` handed back by `useTranslation("app")`. The hand-written
 * `(key, options) => string` shape this helper used to declare is not a
 * supertype of i18next's overloaded `TFunction`, so every call site reported
 * TS2345. That mismatch predates this file's restyle (it is one of a dozen
 * like it in the app); it is corrected here because this helper is now called
 * from two branches and would otherwise report twice.
 */
type AppTranslate = ReturnType<typeof useTranslation<"app">>["t"];

function getEvidenceValue(fact: EvidenceFact, t: AppTranslate): string {
  if (fact.kind === "numeric") {
    if (fact.base.isSuppressed || fact.value === null) {
      return t("studyReport.analyst.suppressed");
    }
    return `${fact.value}${fact.unit === "%" ? "%" : ` ${fact.unit}`}`;
  }
  if (fact.kind === "quote") {
    return fact.quote;
  }
  if (fact.kind === "respondent") {
    return t("studyReport.analyst.evidenceScore", {
      respondentId: fact.respondentId,
      score: fact.score,
      rationale: fact.rationale,
    });
  }
  return fact.text;
}

export function AnalystEvidence(props: AnalystEvidenceProps) {
  const { evidence, messageToken, numbering, open, onOpenChange, focusFactId } =
    props;
  const { t } = useTranslation("app");

  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [highlightedFactId, setHighlightedFactId] = useState<string | null>(
    null,
  );

  // Keep the native <details> element in sync when `open` changes from
  // outside a user click on the summary (e.g. a fact chip in the answer
  // text). Only assign `.open` when the DOM state actually disagrees with the
  // prop, so this never fights the onToggle handler below in a loop.
  useEffect(() => {
    const detailsEl = detailsRef.current;
    if (detailsEl && detailsEl.open !== open) {
      detailsEl.open = open;
    }
  }, [open]);

  // Scroll a newly-focused row into view and flash a highlight on it. A
  // no-op when the row is not mounted (unknown factId, or the disclosure has
  // no matching evidence). Any pending highlight timer is cleared on
  // re-trigger and on unmount.
  useEffect(() => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
    if (!focusFactId) {
      return undefined;
    }
    const rowEl = rowRefs.current.get(focusFactId);
    if (!rowEl) {
      return undefined;
    }
    rowEl.scrollIntoView({ block: "nearest" });
    setHighlightedFactId(focusFactId);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedFactId(null);
      highlightTimeoutRef.current = null;
    }, FOCUS_HIGHLIGHT_MS);
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, [focusFactId]);

  if (numbering.placedFactIds.length === 0) {
    return null;
  }

  // The service persists the whole synthesis bag. Sources only list facts
  // the visible answer cited (chips and rows share `placedFactIds`).
  const citedFactIds = new Set(numbering.placedFactIds);
  const listedEvidence = evidence.filter((fact) => citedFactIds.has(fact.factId));
  if (listedEvidence.length === 0) {
    return null;
  }

  // Rows read in number order, not in the order the service happened to emit
  // the facts. `buildFactNumbering` numbers by first appearance in the prose,
  // so rendering raw `evidence` order makes the badges run 6, 11, 5, 10 down
  // the list. Stable, so two rows sharing a factId keep their relative order.
  const orderedEvidence = [...listedEvidence].sort(
    (first, second) =>
      (numbering.numberByFactId.get(first.factId) ?? Number.MAX_SAFE_INTEGER) -
      (numbering.numberByFactId.get(second.factId) ?? Number.MAX_SAFE_INTEGER),
  );

  // One row per breakdown. Crosstab cells share a source id; demographic
  // buckets share an attribute key (`evidenceSourceKey`). Citation order
  // inside a group is unchanged.
  const factsBySourceKey = new Map<string, Array<EvidenceFact>>();
  for (const fact of orderedEvidence) {
    const sourceKey = evidenceSourceKey(fact);
    const group = factsBySourceKey.get(sourceKey);
    if (group) {
      group.push(fact);
    } else {
      factsBySourceKey.set(sourceKey, [fact]);
    }
  }

  const orderedSourceKeys = Array.from(factsBySourceKey.keys()).sort((a, b) => {
    const firstFactA = factsBySourceKey.get(a)![0];
    const firstFactB = factsBySourceKey.get(b)![0];
    return (
      (numbering.numberByFactId.get(firstFactA.factId) ?? Number.MAX_SAFE_INTEGER) -
      (numbering.numberByFactId.get(firstFactB.factId) ?? Number.MAX_SAFE_INTEGER)
    );
  });

  function handleToggle(event: SyntheticEvent<HTMLDetailsElement>) {
    const domOpen = event.currentTarget.open;
    if (domOpen !== open) {
      onOpenChange(domOpen);
    }
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`analyst-evidence-${messageToken}`}
    >
      <details
        ref={detailsRef}
        open={open}
        onToggle={handleToggle}
        className="rounded-lg border border-border px-3 py-2"
        data-testid={`analyst-provenance-${messageToken}`}
      >
        <summary
          className="cursor-pointer text-xs font-medium text-muted-foreground"
          data-testid={`analyst-provenance-toggle-${messageToken}`}
        >
          {t("studyReport.analyst.evidenceSources")}
        </summary>
        <div className="mt-3 flex flex-col gap-3">
          {orderedSourceKeys.map((sourceKey) => {
            const factsInSource = factsBySourceKey.get(sourceKey)!;
            const firstFact = factsInSource[0];
            const sourceLabel = evidenceGroupTitle(factsInSource);
            
            // For sources with a single fact, show the full fact detail
            if (factsInSource.length === 1) {
              const fact = firstFact;
              const number = numbering.numberByFactId.get(fact.factId);
              return (
                <div
                  key={`${sourceKey}-${fact.factId}`}
                  ref={(el) => {
                    if (el) {
                      rowRefs.current.set(fact.factId, el);
                    } else {
                      rowRefs.current.delete(fact.factId);
                    }
                  }}
                  data-testid={`analyst-evidence-row-${fact.factId}`}
                  className={cn(
                    "flex flex-col gap-1 text-xs",
                    highlightedFactId === fact.factId && "bg-primary/10",
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    {number !== undefined && (
                      <span
                        className={FACT_NUMBER_BADGE_CLASS}
                        aria-hidden="true"
                        data-testid={`analyst-evidence-number-${fact.factId}`}
                      >
                        {number}
                      </span>
                    )}
                    <span className="font-medium text-foreground">
                      {fact.label}
                    </span>
                  </div>
                  {fact.kind === "quote" ? (
                    <blockquote className="my-1 border-l-2 border-border/60 pl-4 italic text-muted-foreground">
                      {getEvidenceValue(fact, t)}
                    </blockquote>
                  ) : (
                    <span className="text-muted-foreground">
                      {getEvidenceValue(fact, t)}
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {t("studyReport.analyst.evidenceSource", {
                      label: sourceLabel,
                    })}
                  </span>
                  {fact.kind === "numeric" && (
                    <span className="text-muted-foreground">
                      {t("studyReport.analyst.evidenceBase", {
                        count: fact.base.n,
                        computation: formatAnalystComputation(fact.computation, t),
                      })}
                    </span>
                  )}
                  {fact.kind === "quote" && (
                    <span className="text-muted-foreground">
                      {t("studyReport.analyst.evidenceSyntheticQuote", {
                        title: fact.label,
                      })}
                    </span>
                  )}
                  {fact.kind === "respondent" && (
                    <span className="text-muted-foreground">
                      {t("studyReport.analyst.evidenceSyntheticTwin", {
                        respondentId: fact.respondentId,
                        persona: fact.personaName
                          ? t("studyReport.analyst.evidencePersona", {
                              name: fact.personaName,
                            })
                          : "",
                      })}
                    </span>
                  )}
                  {fact.warnings.map((warning) => (
                    <span key={warning} className="text-chart-warning">
                      {warning}
                    </span>
                  ))}
                </div>
              );
            }
            
            const factNumbers = Array.from(
              new Set(
                factsInSource
                  .map((fact) => numbering.numberByFactId.get(fact.factId))
                  .filter((num): num is number => num !== undefined),
              ),
            ).sort((a, b) => a - b);
            const highlighted = factsInSource.some(
              (fact) => fact.factId === highlightedFactId,
            );

            return (
              <div
                key={sourceKey}
                ref={(el) => {
                  for (const fact of factsInSource) {
                    if (el) {
                      rowRefs.current.set(fact.factId, el);
                    } else {
                      rowRefs.current.delete(fact.factId);
                    }
                  }
                }}
                data-testid={`analyst-evidence-group-${sourceKey}`}
                className={cn(
                  "flex flex-col gap-1 text-xs",
                  highlighted && "bg-primary/10",
                )}
              >
                <div className="flex items-center gap-1.5">
                  {factNumbers.length > 0 && (
                    <span
                      className={FACT_NUMBER_BADGE_CLASS}
                      aria-hidden="true"
                      data-testid={`analyst-evidence-numbers-${sourceKey}`}
                    >
                      {factNumbers.length === 1
                        ? factNumbers[0]
                        : `${factNumbers[0]}-${factNumbers[factNumbers.length - 1]}`}
                    </span>
                  )}
                  <span className="font-medium text-foreground">
                    {sourceLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
}
